import { baseClient } from "./baseRequest";

export type MomentRecurrence = "yearly" | "once";

// An entry of the Moments whitelist shown in the app's "Add a moment" picker.
export type MomentOccasion = {
  key: string;
  label: string;
  /** MaterialCommunityIcons name shown in the app list. */
  icon: string;
  /** Used for the AI pendulum art and the semantic klydo search. */
  searchPhrase: string;
  /** At least one: the random pick, and the default of the semantic pick. */
  klydoIds: string[];
  /** Default pendulum logo (transparent PNG): fallback and old-firmware logo. */
  logoUrl?: string;
  active: boolean;
  order: number;
};

export type CreateMomentOccasionPayload = Pick<
  MomentOccasion,
  "key" | "label" | "icon" | "searchPhrase"
> &
  Pick<MomentOccasion, "klydoIds"> &
  Partial<Pick<MomentOccasion, "active" | "order">>;

export type UpdateMomentOccasionPayload = Partial<
  Omit<MomentOccasion, "key" | "logoUrl">
>;

// A moment a clock's owner created in the app.
export type ClockMoment = {
  id: string;
  clockId: string;
  occasionKey: string;
  date: { month: number; day: number; year?: number };
  recurrence: MomentRecurrence;
  title: string;
  description: string;
  klydoId: string;
  /** Semantic mode: "pending" while the semantic pick is still running. */
  klydoStatus?: "pending" | "done";
  pendulumLogoUrl?: string;
  logoSource?: "generated" | "occasion";
  /** The personalised picture is generated after the moment is saved. */
  logoStatus?: "pending" | "generating" | "done";
  timeZone?: string;
  lastDeliveredDateKey?: number;
  createdAt: string;
};

// How a moment's klydo is chosen: at random from the occasion's list, or by a
// semantic search on the occasion's search phrase (the list is the default).
export type MomentKlydoSelection = "random" | "semantic";

// Settings of the app's Moments tab.
export type MomentSettings = {
  klydoSelection: MomentKlydoSelection;
  /** The hero photo of the tab. */
  coverUrl?: string;
  // Absent when there is no cover.
  coverType?: "image" | "video";
  // Video covers: a still frame of the video.
  coverPosterUrl?: string;
};

export type MomentsApiClient = {
  getSettings: () => Promise<MomentSettings>;
  uploadCover: (file: string) => Promise<MomentSettings>;
  clearCover: () => Promise<MomentSettings>;
  setKlydoSelection: (mode: MomentKlydoSelection) => Promise<MomentSettings>;
  getOccasions: () => Promise<MomentOccasion[]>;
  createOccasion: (
    payload: CreateMomentOccasionPayload,
  ) => Promise<MomentOccasion>;
  updateOccasion: (
    key: string,
    payload: UpdateMomentOccasionPayload,
  ) => Promise<MomentOccasion>;
  deleteOccasion: (key: string) => Promise<void>;
  uploadOccasionLogo: (key: string, file: string) => Promise<MomentOccasion>;
  getClockMoments: (clockId: string) => Promise<ClockMoment[]>;
  deleteMoment: (momentId: string) => Promise<void>;
};

const momentsClient: MomentsApiClient = {
  getSettings: () =>
    baseClient.get<MomentSettings>({ url: "/admin/moment-settings" }),
  uploadCover: (file) =>
    baseClient.post<MomentSettings>({
      url: "/admin/moment-settings/cover",
      body: { file },
    }),
  clearCover: () =>
    baseClient.delete<MomentSettings>({ url: "/admin/moment-settings/cover" }),
  setKlydoSelection: (klydoSelection) =>
    baseClient.put<MomentSettings>({
      url: "/admin/moment-settings/klydo-selection",
      body: { klydoSelection },
    }),
  getOccasions: () =>
    baseClient.get<MomentOccasion[]>({ url: "/admin/moment-occasions" }),
  createOccasion: (payload) =>
    baseClient.post<MomentOccasion>({
      url: "/admin/moment-occasions",
      body: payload,
    }),
  updateOccasion: (key, payload) =>
    baseClient.put<MomentOccasion>({
      url: `/admin/moment-occasions/${key}`,
      body: payload,
    }),
  deleteOccasion: (key) =>
    baseClient.delete<void>({ url: `/admin/moment-occasions/${key}` }),
  uploadOccasionLogo: (key, file) =>
    baseClient.post<MomentOccasion>({
      url: `/admin/moment-occasions/${key}/logo`,
      body: { file },
    }),
  getClockMoments: (clockId) =>
    baseClient.get<ClockMoment[]>({
      url: "/admin/moments",
      params: { clockId },
    }),
  deleteMoment: (momentId) =>
    baseClient.delete<void>({ url: `/admin/moments/${momentId}` }),
};

export default momentsClient;
